import { useState } from "react";
import { useCategoriesData } from "@/features/categories/hooks/useCategoriesData";
import { useCategoryMutations } from "@/features/categories/hooks/useCategoryMutations";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Edit, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Category } from "@/features/categories/model";
import { useTranslation } from "@/shared/i18n";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/shared/ui/alert-dialog";
import { Pagination, DataTable } from "@/shared/widgets";

const CategoriesList = () => {
    const { t } = useTranslation('categories');
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const { data, isLoading, isError } = useCategoriesData({
        type: "",
        page: currentPage,
        limit: pageSize
    });

    const { deleteCategory } = useCategoryMutations();


    const handleEdit = (categoryId: string) => {
        navigate(`/categories/edit/${categoryId}`);
    };

    const handleDelete = (categoryId: string) => {
        deleteCategory.mutate({ id: categoryId });
    };

    const getTypeBadgeVariant = (type: string) => {
        switch (type) {
            case "PRESET":
                return "default";
            case "LEARNING":
                return "secondary";
            case "TEMPLATE":
                return "success";
            default:
                return "outline";
        }
    };

    const getTypeDisplayName = (type: string) => {
        switch (type) {
            case "PRESET":
                return t('preset');
            case "LEARNING":
                return t('learning');
            case "TEMPLATE":
                return t('template');
            default:
                return type;
        }
    };

    const categories = data?.data || [];

    const formattedPagination = {
        page: data?.page || 1,
        limit: data?.limit || 10,
        totalPage: data?.totalPage || 1,
        totalItems: data?.totalItems || 0,
    }


    return (
        <>
            <DataTable
                data={categories}
                isLoading={isLoading}
                error={isError ? new Error(t('errorLoading')) : null}
                emptyTitle={t('emptyTitle')}
                emptyDescription={() => t('emptyDescription')}
                errorMessage={t('errorMessage')}
                columns={[
                    {
                        header: t('name'),
                        cell: (category: Category) => (
                            <div className="truncate max-w-xs" title={category.name}>
                                <span className="text-white">{category.name}</span>
                            </div>
                        ),
                    },
                    {
                        header: t('type'),
                        cell: (category: Category) => (
                            <Badge variant={getTypeBadgeVariant(category.type)}>
                                {getTypeDisplayName(category.type)}
                            </Badge>
                        ),
                    },
                    {
                        header: t('order'),
                        cell: (category: Category) => (
                            <span className="text-white/60">
                                {category.order !== null && category.order !== undefined ? category.order : '-'}
                            </span>
                        ),
                    },
                    {
                        header: t('createdAt'),
                        cell: (category: Category) => (
                            <span className="text-white/60">
                                {category.createdAt ? new Date(category.createdAt).toLocaleDateString('pt-BR') : '-'}
                            </span>
                        ),
                    },
                    {
                        header: t('actions'),
                        cell: (category: Category) => (
                            <div className="flex justify-end gap-2">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleEdit(category.id!)}
                                    title={t('edit')}
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="text-destructive hover:text-destructive"
                                            title={t('delete')}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent className="bg-[#1A1A1A] border-[#333333]">
                                        <AlertDialogHeader>
                                            <AlertDialogTitle className="text-white">
                                                {t('confirmDelete')}
                                            </AlertDialogTitle>
                                            <AlertDialogDescription className="text-gray-400">
                                                {t('deleteMessage', { name: category.name })}
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel className="bg-[#333333] text-white border-[#444444] hover:bg-[#444444]">
                                                {t('cancel')}
                                            </AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => handleDelete(category.id!)}
                                                className="bg-red-600 text-white hover:bg-red-700"
                                            >
                                                {t('delete')}
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        ),
                        className: "text-right",
                    },
                ]}
            />

            {/* Widget de paginação */}
            {!isLoading && !isError && formattedPagination.totalPage > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={formattedPagination?.totalPage}
                    setCurrentPage={setCurrentPage}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    showPageSizeOptions={true}
                    totalItems={formattedPagination?.totalItems}
                    showTotalItems={true}
                    syncWithQueryParams={false}
                />
            )}
        </>
    );
};

export default CategoriesList;